import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
	OwnerOrganizationDepartmentContacts,
	OwnerOrganizationDepartmentKind,
	User,
} from '@/reservation/reservation.model';
import { OrganizationService } from '@/organization/organization/organization.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { ContactDataInterface, OrganizationContactAddEditDialog } from '@/organization/organization/organization.model';
import { PageFunction } from '@/common/enums/PageFunction';

@Component({
	selector: 'app-organization-contact-add-edit-dialog',
	templateUrl: './organization-contact-add-edit-dialog.component.html',
	styleUrls: ['./organization-contact-add-edit-dialog.component.css'],
})
export class OrganizationContactAddEditDialogComponent implements OnInit {
	formContact: FormGroup = this.formBuilder.group({
		kind: new FormControl(null, [Validators.required]),
		userResponsibility: new FormControl(null, [Validators.required]),
		departmentContacts: this.formBuilder.array([]),
	});

	pageFunction: PageFunction;
	submitButtonText = '';
	departmentKinds = [];
	departmentUsers = [];

	constructor(
		@Inject(MAT_DIALOG_DATA) private readonly data: OrganizationContactAddEditDialog,
		private readonly dialogRef: MatDialogRef<OrganizationContactAddEditDialogComponent>,
		private readonly formBuilder: FormBuilder,
		private readonly organizationService: OrganizationService,
		private readonly infobox: InfoboxService,
	) {}

	ngOnInit(): void {
		this.departmentKinds = this.organizationService.enterpriseData.departmentKinds;
		this.departmentUsers = this.organizationService.enterpriseData.departmentUsers;
		this.pageFunction = this.data?.mode;
		this.submitButtonText = this.pageFunction === PageFunction.EDIT ? 'Zapisz adres' : 'Dodaj adres';
		if (this.pageFunction === PageFunction.EDIT) {
			this.initializeFormFill();
		}
	}

	cancelForm(): void {
		this.dialogRef.close(false);
	}

	submitForm(): void {
		this.formContact.markAllAsTouched();
		if (!this.formContact.valid) return;
		if (!this.checkIfContactsAreValid()) return;

		const isDepartmentAlreadyExistOnOrganization: boolean =
			this.organizationService.organizationData.departments.some((kind) => {
				if (this.pageFunction === PageFunction.EDIT) {
					return kind.kind.id === this.formContact.controls.kind.value.id && kind.id !== this.data.id;
				} else {
					return kind.kind.id === this.formContact.controls.kind.value.id;
				}
			});
		if (isDepartmentAlreadyExistOnOrganization) {
			this.infobox.error('Dział o takiej nazwie już jest dodany do organizacji!');
			return;
		}

		const departmentContacts = this.getDepartmentContactsValues();
		let organizationContactData: ContactDataInterface = {
			kind: this.formContact.controls.kind.value,
			departmentContacts: departmentContacts,
			userResponsibility: this.formContact.controls.userResponsibility.value,
		};

		if (this.pageFunction === PageFunction.EDIT) {
			organizationContactData = {
				...organizationContactData,
				id: this.data.id,
			};
		}
		this.dialogRef.close(organizationContactData);
	}

	addSubContacts(): void {
		this.contacts.controls.push(this.createContactsForm());
	}

	removeSubContact(index: number): void {
		this.contacts.removeAt(index);
	}

	organizationContactComparison(
		option: OwnerOrganizationDepartmentKind | User,
		value: OwnerOrganizationDepartmentKind | User,
	): boolean {
		return option.id === value.id;
	}

	get contacts(): FormArray {
		return this.formContact.controls['departmentContacts'] as FormArray;
	}

	private createContactsForm(): FormGroup {
		return new FormGroup({
			email: new FormControl('', [Validators.required, Validators.email]),
			phone: new FormControl('', [Validators.required]),
		});
	}

	private initializeFormFill(): void {
		const contacts = this.data.organizationData.departmentContacts;
		contacts.forEach(() => this.addSubContacts());
		this.formContact.patchValue(this.data.organizationData);
	}

	private checkIfContactsAreValid(): boolean {
		return this.contacts.controls.every((control) => control.valid);
	}

	private getDepartmentContactsValues(): OwnerOrganizationDepartmentContacts[] {
		return (this.formContact.controls.departmentContacts as FormArray).controls.map((contact) => {
			return contact.value;
		});
	}
}
